import moment from 'moment-timezone';
import { PayloadAction } from '@reduxjs/toolkit';
import {
  Appointment,
  AssistantMessage,
  PrescriptionMedication,
  Prescription,
  RunSavedPromptRequest,
  PhysicianContact,
  ReferralRequest,
} from 'generated/api';
import { endpointIsLoading } from 'careand-redux/actions/endpointLoading';
import PerformanceUtils from 'services/PerformanceUtils';
import { createResettableSlice } from 'careand-redux/utils/makeResettable';
import { showOldVersion } from 'careand-redux/actions/showOldVersion';
import { unwrap } from 'careand-redux/utils/unwrap';
import _ from 'lodash';
import { v4 as uuidV4 } from 'uuid';

export type HomeMode = 'home' | 'search';

export interface UIState {
  screenTitle?: string;
  loading: boolean;
  loadingData: number;
  loadingEndpoints: Record<string, { id: string; params?: any }[]>;
  oldVersion: boolean;
  sideMenuActive: boolean;
  bubbles: Bubble[];
  activeBubble?: Bubble;
  pinned: Record<string, PinnedDocument[] | undefined>;
  activeLinkedAccount: Record<string, string | undefined>;
  phone?: {
    number: string;
    patientId: string;
    appointmentId?: string;
  };
  transcription?: {
    patientId: string;
    appointmentId: string;
    status: string;
    minimized?: boolean;
  };
  homeMode: HomeMode;
  homeSearch: string;
  reorderingTabBar: boolean;
  popupRegistry: Partial<Record<string, boolean>>;
  uiOptions: string[];
  assistant: boolean;
  assistantUnread: number;
  assistantMessage?: string;
  assistants: ReduxAssistantState[];
  openAssistantId?: string;
  savedPromptData?: any;
  medicationSuggestionsByAppointmentId: Partial<Record<string, MedicationSuggestions>>;
  templateSuggestionsByAppointmentId: Partial<Record<string, TemplateSuggestions>>;
}

type MedicationSuggestions = {
  appointmentId: string;
  medications: PrescriptionMedication[];
  date: number;
  isLoading: boolean;
};

type TemplateSuggestions = {
  appointmentId: string;
  templateIds: string[];
  date: number;
  isLoading: boolean;
};

export type ReduxAssistantState = {
  id: string;
  unread: number;
  messages: AssistantMessage[];
  loading?: boolean;
  firstMessage?:
    | { type: 'promptData'; savedPromptData: RunSavedPromptRequest }
    | { type: 'message'; assistantMessage: string };
  metadata?: {
    patientId?: string;
    documentId?: string;
  };
  patient_id?: string;
  subject?: string;
};

type PinnedNewReferral = {
  type: 'referral';
  text?: string;
  send_to?: PhysicianContact | null;
};

type PinnedNewReferralRequest = {
  type: 'referral_request';
  referralRequest: Partial<ReferralRequest>;
};

type PinnedTemplate = {
  type: 'template';
  id: string;
  noteForm?: string;
  documentId?: string;
  title?: string;
  overrideTitle?: string;
  defaultSavedDocumentCategory?: string;
  overrideId?: string;
  payload?: {
    templateId?: string;
    title?: string;
    prefilledValues?: Record<string, string>;
  };
};
type PinnedPatientDocument = {
  type: 'document';
  patientId: string;
  id: string;
  startShowingDetails?: boolean;
};
type PinnedVitals = {
  type: 'vitals_monitor';
  id: 'vitals';
  patientId: string;
};
type PinnedSendMultipleDocuments = {
  type: 'send_multiple_documents';
  patientId: string;
};
type PinnedCalculator = {
  type: 'calculator';
  patientId: string;
  calculatorType: string;
  overrideTitle?: string;
  title: string;
};

type PinnedReqsList = {
  type: 'reqs_list';
  patientId: string;
};
type PinnedMetrics = {
  type: 'metrics';
  patientId: string;
  metric?: string;
};
type PinnedPrescription = {
  type: 'prescription';
  id?: string;
  prescription: Partial<Prescription>;
};

export type PinnedDocument =
  | PinnedNewReferral
  | PinnedNewReferralRequest
  | PinnedTemplate
  | PinnedPatientDocument
  | PinnedVitals
  | PinnedSendMultipleDocuments
  | PinnedReqsList
  | PinnedMetrics
  | PinnedCalculator
  | PinnedPrescription;

type PinnedDocumentAction = PinnedDocument & { patientId: string };

interface RemovePinnedDocumentAction {
  patientId: string;
  id: string;
}

export interface Bubble {
  id: string;
  chatId?: string;
  appointment_id?: string;
  appointment_type?: string;
  patient_id?: string;
  booked_by?: string;
  appointment?: Appointment;
  description?: string;
  type?: string;
  chat?: boolean;
  assistant?: boolean;
  date?: number;
  payload?: BubblePayload;
  token?: string;
  manual?: boolean;
}

export interface BubblePayload {
  room?: string;
}

export interface ToggleAssistantPayload {
  assistant: boolean;
  assistantMessage?: string;
  savedPromptData?: any;
}

export type Phone = Required<UIState>['phone'];
export type Transcription = Required<UIState>['transcription'];

const initialState: UIState = {
  loading: false,
  loadingData: 0,
  loadingEndpoints: {},
  oldVersion: false,
  sideMenuActive: false,
  bubbles: [],
  pinned: {},
  activeLinkedAccount: {},
  homeMode: 'home',
  homeSearch: '',
  reorderingTabBar: false,
  uiOptions: [],
  popupRegistry: {},
  assistant: false,
  assistantUnread: 0,
  assistantMessage: '',
  savedPromptData: undefined,
  assistants: [],
  medicationSuggestionsByAppointmentId: {},
  templateSuggestionsByAppointmentId: {},
};

export const makeInitialUIState = () => initialState;

const uiSlice = createResettableSlice({
  name: 'UI',
  initialState,
  reducers: {
    addMedicationSuggestion: unwrap(
      (state, value: { appointmentId: string; medication: PrescriptionMedication }) => {
        state.medicationSuggestionsByAppointmentId[value.appointmentId]?.medications.push(
          value.medication,
        );
      },
    ),
    updateMedicationSuggestions: unwrap(
      (state, value: Partial<MedicationSuggestions> & { appointmentId: string }) => {
        const originalSuggestion = state.medicationSuggestionsByAppointmentId[value.appointmentId];
        const newSuggestion = originalSuggestion
          ? Object.assign(originalSuggestion, value)
          : {
              medications: value.medications ?? [],
              date: value.date ?? moment().unix(),
              isLoading: value.isLoading ?? false,
              appointmentId: value.appointmentId,
            };
        state.medicationSuggestionsByAppointmentId[value.appointmentId] = newSuggestion;
      },
    ),
    updateTemplateSuggestions: unwrap(
      (state, value: Partial<TemplateSuggestions> & { appointmentId: string }) => {
        const originalSuggestion = state.templateSuggestionsByAppointmentId[value.appointmentId];
        const newSuggestion = originalSuggestion
          ? Object.assign(originalSuggestion, value)
          : {
              templateIds: value.templateIds ?? [],
              date: value.date ?? moment().unix(),
              isLoading: value.isLoading ?? false,
              appointmentId: value.appointmentId,
            };
        state.templateSuggestionsByAppointmentId[value.appointmentId] = newSuggestion;
      },
    ),
    toggleUIOption: unwrap((state, value: string) => {
      let exists = state.uiOptions.indexOf(value) > -1;
      if (exists) {
        state.uiOptions = state.uiOptions.filter((val) => val !== value);
      } else {
        state.uiOptions.push(value);
      }
    }),
    setAssistantUnread: unwrap((state, value: number) => {
      state.assistantUnread = value;
    }),
    toggleAssistant: (state, action: PayloadAction<ToggleAssistantPayload>) => {
      let { assistant, assistantMessage, savedPromptData } = action.payload;
      state.assistant = assistant !== undefined ? assistant : !state.assistant;
      state.assistantMessage = assistantMessage;
      state.savedPromptData = savedPromptData;
    },
    addAssistant: unwrap(
      (
        state,
        payload: Omit<ReduxAssistantState, 'messages' | 'unread' | 'id'> & {
          setActive?: boolean;
        },
      ) => {
        const id = uuidV4();
        const { setActive } = payload;
        payload.setActive = undefined;
        if (payload.firstMessage?.type === 'promptData') {
          payload.metadata ??= {};
          payload.metadata.patientId ??=
            payload.firstMessage.savedPromptData.savedPromptRequest?.patient_id;
          payload.metadata.documentId ??=
            payload.firstMessage.savedPromptData.savedPromptRequest?.document_id;
        }
        state.assistants.push({ ...payload, id, messages: [], unread: 0 });
        if (!state.openAssistantId || setActive) {
          state.openAssistantId = id;
        }
      },
    ),
    addSavedAssistants: unwrap((state, payload: ReduxAssistantState[]) => {
      payload.forEach((conversation) => {
        state.assistants = PerformanceUtils.editOrAdd(conversation, state.assistants);
      });
    }),
    removeAssistant: unwrap((state, id: string) => {
      const idx = _.findIndex(state.assistants, (it) => it.id === id);
      if (idx === -1) {
        return;
      }
      state.assistants.splice(idx, 1);
    }),
    updateAssistant: unwrap((state, payload: Partial<ReduxAssistantState>) => {
      const original = state.assistants.find((it) => it.id === payload.id);
      if (!original) {
        return;
      }

      let messagesDiff = 0;

      if (
        state.openAssistantId !== original.id &&
        payload.unread === undefined &&
        !!payload.messages
      ) {
        messagesDiff =
          original.messages && payload.messages?.length !== original.messages.length ? 1 : 0;
      }

      Object.assign(original, payload);

      if (messagesDiff) {
        original.unread += messagesDiff;
      }

      if (payload.messages) {
        original.messages = payload.messages;
      }
    }),
    quickOpenAssistant: unwrap((state) => {
      if (state.openAssistantId) {
        state.openAssistantId = undefined;
      } else {
        if (state.assistants.length > 0) {
          state.openAssistantId = _.last(state.assistants)!.id;
        } else {
          const id = uuidV4();
          const newAssistant: ReduxAssistantState = {
            id,
            messages: [],
            unread: 0,
          };
          state.assistants.push(newAssistant);
          state.openAssistantId = id;
        }
      }
    }),
    showAssistant: unwrap((state, id: string | undefined) => {
      state.openAssistantId = id;
    }),
    setReorderingTabBar: unwrap((state, reordering: boolean) => {
      state.reorderingTabBar = reordering;
    }),
    toggleReorderingTabBar: (state) => {
      state.reorderingTabBar = !state.reorderingTabBar;
    },
    updateHomeSearch: (state: UIState, action: PayloadAction<string>) => {
      state.homeSearch = action.payload;
    },
    setHomeMode: (state, action: PayloadAction<HomeMode>) => {
      state.homeMode = action.payload;
    },
    setBubbles: (state, action: PayloadAction<Bubble[]>) => {
      state.bubbles = action.payload;
    },
    setActiveBubble: (state, action: PayloadAction<Bubble>) => {
      state.activeBubble = action.payload;
    },
    setActiveBubbleId: (state, action: PayloadAction<string>) => {
      state.activeBubble = state.bubbles.find((it) => it.id === action.payload);
    },
    setActiveLinkedAccount: (state, action: PayloadAction<string>) => {
      state.activeLinkedAccount[state.activeBubble!.id] = action.payload;
    },
    setScreenTitle: (state, action: PayloadAction<string>) => {
      state.screenTitle = action.payload;
    },
    removePinnedDocument: (state, action: PayloadAction<RemovePinnedDocumentAction>) => {
      const { id, patientId } = action.payload;
      state.pinned[patientId] = PerformanceUtils.removeFromArray({ id }, state.pinned[patientId]);
    },
    addPinnedDocument: (state: UIState, action: PayloadAction<PinnedDocumentAction>) => {
      const payload = action.payload;
      console.log('OPEN', payload);
      state.pinned[payload.patientId] = PerformanceUtils.editOrAdd(
        action.payload,
        state.pinned[payload.patientId],
      );
    },
    setLoading: (state, action: PayloadAction<boolean>) => {
      state.loading = action.payload;
    },
    setLoadingData: (state, action: PayloadAction<boolean>) => {
      state.loadingData += action.payload ? 1 : -1;
    },
    setSideMenu: (state, action: PayloadAction<boolean>) => {
      state.sideMenuActive = action.payload;
    },
    toggleSideMenu: (state) => {
      state.sideMenuActive = !state.sideMenuActive;
    },
    openPhoneScreen: (state, action: PayloadAction<Phone>) => {
      state.phone = action.payload;
    },
    closePhoneScreen: (state) => {
      state.phone = undefined;
    },
    openTranscriptionScreen: (state, action: PayloadAction<Transcription>) => {
      state.transcription = action.payload;
    },
    closeTranscriptionScreen: (state) => {
      state.transcription = undefined;
    },
    toggleTranscriptionMinimized: (state) => {
      if (!state.transcription) {
        return;
      }
      state.transcription.minimized = !state.transcription.minimized;
    },
    updateTranscriptionStatus: (state, action: PayloadAction<string>) => {
      if (!state.transcription) {
        return;
      }
      state.transcription.status = action.payload;
    },
    popupToggled: (state, action: PayloadAction<{ popup: string; open?: boolean }>) => {
      const { popup, open } = action.payload;
      if (typeof open === undefined) {
        state.popupRegistry[popup] = !(state.popupRegistry[popup] ?? false);
      } else {
        state.popupRegistry[popup] = open;
      }
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(endpointIsLoading, (state, action) => {
        const endpointId = JSON.stringify({
          name: action.payload.name,
          params: action.payload.params,
        });
        const isLoading = action.payload.loading ?? true;
        state.loadingData += isLoading ? 1 : -1;
        if (isLoading) {
          state.loadingEndpoints[action.payload.name] =
            state.loadingEndpoints[action.payload.name] ?? [];
          state.loadingEndpoints[action.payload.name]!.push({
            id: endpointId,
            params: action.payload.params,
          });
        } else {
          state.loadingEndpoints[action.payload.name] =
            state.loadingEndpoints[action.payload.name]?.filter((it) => it.id !== endpointId) ?? [];
        }
      })
      .addCase(showOldVersion, (state) => {
        state.oldVersion = true;
      });
  },
});

export const uiActions = uiSlice.actions;
export default uiSlice.reducer;
